if (!customElements.get('course-checkout-form'))
{
   class CourseCheckoutForm extends HTMLElement
   {
      connectedCallback()
      {
         this.form = this.querySelector('form');
         if (!this.form) return;

         this.section = this.closest('.shopify-section, dialog');
         this.quantityInput = this.querySelector('input[name="quantity"]');
         this.variantInput = this.querySelector('input[name="variant_id"]');
         this.countrySelect = this.querySelector('select[name="attributes[nazione]"]');
         this.submitButton = this.querySelector('[data-submit-button]');
         this.submitButtonText = this.querySelector('[data-submit-button-text]');
         this.errorMessage = this.querySelector('[data-error-message]');
         this.participantsContainer = this.querySelector('[data-participants-container]');
         this.addMemberButton = this.querySelector('[data-add-participant="socio"]');
         this.quantitySelector = this.querySelector('quantity-selector-component');
         this.isRegistrationMode = this.dataset.registrationMode === 'true';
         this.isSubmitting = false;
         this.isCheckoutDateValid = this.dataset.checkoutDateValid !== 'false';
         this.isVariantAvailable = !this.submitButton?.disabled;
         this.participants = [];
         this.nextParticipantId = 1;

         this.defaultButtonText = this.submitButtonText?.textContent?.trim() || 'Procedi al checkout';
         this.loadingButtonText = this.getAttribute('data-loading-label') || 'Reindirizzamento...';

         this.handleSubmit = this.handleSubmit.bind(this);
         this.handleVariantUpdate = this.handleVariantUpdate.bind(this);
         this.handleAddMember = this.handleAddMember.bind(this);
         this.handleParticipantInput = this.handleParticipantInput.bind(this);
         this.handleParticipantAction = this.handleParticipantAction.bind(this);
         this.handleQuantityUpdate = this.handleQuantityUpdate.bind(this);
         this.handleQuantityInputChange = this.handleQuantityInputChange.bind(this);

         this.form.addEventListener('submit', this.handleSubmit);
         this.section?.addEventListener('variant:update', this.handleVariantUpdate);
         this.addMemberButton?.addEventListener('click', this.handleAddMember);
         this.participantsContainer?.addEventListener('input', this.handleParticipantInput);
         this.participantsContainer?.addEventListener('click', this.handleParticipantAction);
         this.quantitySelector?.addEventListener('quantity-selector:update', this.handleQuantityUpdate);
         this.quantityInput?.addEventListener('change', this.handleQuantityInputChange);

         this.setDefaultCountry();
         if (this.isRegistrationMode)
         {
            this.syncParticipantsFromQuantity();
         } else
         {
            this.syncQuantityFromParticipants();
         }
         this.renderParticipants();
      }

      disconnectedCallback()
      {
         this.form?.removeEventListener('submit', this.handleSubmit);
         this.section?.removeEventListener('variant:update', this.handleVariantUpdate);
         this.addMemberButton?.removeEventListener('click', this.handleAddMember);
         this.participantsContainer?.removeEventListener('input', this.handleParticipantInput);
         this.participantsContainer?.removeEventListener('click', this.handleParticipantAction);
         this.quantitySelector?.removeEventListener('quantity-selector:update', this.handleQuantityUpdate);
         this.quantityInput?.removeEventListener('change', this.handleQuantityInputChange);
      }

      handleVariantUpdate(event)
      {
         if (!event?.detail) return;

         if (event.detail.data?.newProduct?.id)
         {
            this.dataset.productId = event.detail.data.newProduct.id;
         } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId)
         {
            return;
         }

         const variant = event.detail.resource;
         if (!variant) return;

         if (this.variantInput)
         {
            this.variantInput.value = variant.id || '';
         }

         if (this.submitButton)
         {
            this.isVariantAvailable = Boolean(variant.available);
         }

         if (this.quantityInput)
         {
            const max = variant.quantity_rule?.max;
            this.quantityInput.min = '1';
            this.quantityInput.step = '1';

            if (max !== null && max !== undefined)
            {
               this.quantityInput.max = String(max);
            } else
            {
               this.quantityInput.removeAttribute('max');
            }
         }

         this.enforceParticipantLimits();
         if (this.isRegistrationMode)
         {
            this.syncParticipantsFromQuantity();
         } else
         {
            this.syncQuantityFromParticipants();
         }
         this.renderParticipants();
         this.updateSubmitButtonState();
      }

      setDefaultCountry()
      {
         if (!this.countrySelect) return;
         const preferred = this.dataset.defaultCountry || 'Italy';
         const fallback = preferred === 'Italy' ? 'Italia' : 'Italy';

         if (this.countrySelect.value && this.countrySelect.value !== '---') return;

         if (this.countrySelect.querySelector(`option[value="${preferred}"]`))
         {
            this.countrySelect.value = preferred;
            return;
         }

         if (this.countrySelect.querySelector(`option[value="${fallback}"]`))
         {
            this.countrySelect.value = fallback;
         }
      }

      getQuantity()
      {
         const parsedQuantity = Number.parseInt(this.quantityInput?.value || '1', 10);
         return Number.isFinite(parsedQuantity) && parsedQuantity > 0 ? parsedQuantity : 1;
      }

      getAvailability()
      {
         const availability = Number.parseInt(this.dataset.availability || '', 10);
         return Number.isFinite(availability) && availability >= 0 ? availability : null;
      }

      getMaxQuantity()
      {
         const variantMax = Number.parseInt(this.quantityInput?.getAttribute('max') || '', 10);
         const limits = [
            this.getAvailability(),
            Number.isFinite(variantMax) && variantMax > 0 ? variantMax : null,
         ].filter((limit) => limit !== null);

         return limits.length > 0 ? Math.min(...limits) : null;
      }

      canIncreaseQuantity()
      {
         const maxQuantity = this.getMaxQuantity();
         if (maxQuantity === null) return true;

         return this.participants.length + 2 <= maxQuantity;
      }

      canAddParticipant(type)
      {
         if (!this.canIncreaseQuantity()) return false;

         return true;
      }

      syncQuantityFromParticipants()
      {
         if (!this.quantityInput) return;

         this.quantityInput.value = String(this.participants.length + 1);
      }

      normalizeRegistrationQuantity(quantity)
      {
         const min = Number.parseInt(this.quantityInput?.min || '1', 10);
         const max = this.getMaxQuantity();
         const safeMin = Number.isFinite(min) && min > 0 ? min : 1;
         const parsedQuantity = Number.parseInt(String(quantity || ''), 10);
         const safeQuantity = Number.isFinite(parsedQuantity) ? Math.max(parsedQuantity, safeMin) : safeMin;

         return max === null ? safeQuantity : Math.min(safeQuantity, max);
      }

      syncParticipantsFromQuantity(quantity = this.getQuantity())
      {
         if (!this.isRegistrationMode) return;

         const normalizedQuantity = this.normalizeRegistrationQuantity(quantity);
         const extraCount = Math.max(normalizedQuantity - 1, 0);

         if (this.quantityInput)
         {
            this.quantityInput.value = String(normalizedQuantity);
         }

         if (this.participants.length > extraCount)
         {
            this.participants = this.participants.slice(0, extraCount);
         }

         while (this.participants.length < extraCount)
         {
            this.participants.push({
               id: this.nextParticipantId,
               type: 'iscritto',
               firstName: '',
               lastName: '',
               email: '',
               taxCode: '',
            });
            this.nextParticipantId += 1;
         }
      }

      handleQuantityUpdate(event)
      {
         if (!this.isRegistrationMode) return;

         this.syncParticipantsFromQuantity(event?.detail?.quantity);
         this.renderParticipants();
      }

      handleQuantityInputChange()
      {
         if (!this.isRegistrationMode) return;

         this.syncParticipantsFromQuantity();
         this.renderParticipants();
      }

      enforceParticipantLimits()
      {
         const maxQuantity = this.getMaxQuantity();
         const maxExtras = maxQuantity === null ? null : Math.max(maxQuantity - 1, 0);

         if (maxExtras !== null && this.participants.length > maxExtras)
         {
            this.participants = this.participants.slice(0, maxExtras);
         }
      }

      renderParticipants()
      {
         if (!this.participantsContainer) return;

         if (this.participants.length === 0)
         {
            this.participantsContainer.innerHTML = '';
            this.participantsContainer.classList.add('hidden');
            this.updateActionButtons();
            this.updateSubmitButtonState();
            return;
         }

         this.participantsContainer.innerHTML = '';
         const fragment = document.createDocumentFragment();
         const registrationMembers = this.participants.filter((participant) => participant.type === 'iscritto');
         const members = this.participants.filter((participant) => participant.type === 'socio');

         if (registrationMembers.length > 0)
         {
            fragment.appendChild(this.createParticipantGroup('Iscritti aggiuntivi', registrationMembers));
         }

         if (members.length > 0)
         {
            fragment.appendChild(this.createParticipantGroup('Ospiti aggiuntivi', members));
         }

         this.participantsContainer.appendChild(fragment);
         this.participantsContainer.classList.remove('hidden');
         this.updateActionButtons();
         this.updateSubmitButtonState();
      }

      createParticipantGroup(titleText, participants)
      {
         const group = document.createElement('div');
         group.className = 'course-checkout-block__participant-group';

         const title = document.createElement('p');
         title.className = 'course-checkout-block__participant-group-title';
         title.textContent = titleText;
         group.appendChild(title);

         participants.forEach((participant, index) =>
         {
            group.appendChild(this.createParticipantFieldset(participant, index + 1));
         });

         return group;
      }

      createParticipantFieldset(participant, participantIndex)
      {
         const fieldset = document.createElement('fieldset');
         fieldset.className = 'course-checkout-block__participant';

         const header = document.createElement('div');
         header.className = 'course-checkout-block__participant-header';

         const title = document.createElement('p');
         title.className = 'course-checkout-block__participant-title';
         title.textContent = this.isRegistrationMode
            ? `Iscritto #${participantIndex + 1}`
            : `Ospite aggiuntivo #${participantIndex}`;
         header.appendChild(title);

         if (!this.isRegistrationMode)
         {
            const removeButton = document.createElement('button');
            removeButton.type = 'button';
            removeButton.className = 'course-checkout-block__participant-remove button-unstyled';
            removeButton.dataset.removeParticipant = String(participant.id);
            removeButton.textContent = 'Rimuovi';
            header.appendChild(removeButton);
         }

         fieldset.appendChild(header);

         const fields = document.createElement('div');
         fields.className = 'course-checkout-block__participant-fields';

         fields.appendChild(this.createParticipantField(participant, 'firstName', 'Nome', 'given-name'));
         fields.appendChild(this.createParticipantField(participant, 'lastName', 'Cognome', 'family-name'));
         if (this.isRegistrationMode)
         {
            fields.appendChild(this.createParticipantField(participant, 'email', 'E-mail', 'email', 'email'));
            fields.appendChild(this.createParticipantField(participant, 'taxCode', 'Codice Fiscale', 'off'));
         }

         fieldset.appendChild(fields);
         return fieldset;
      }

      createParticipantField(participant, fieldName, labelText, autocomplete, inputType = 'text')
      {
         const wrapper = document.createElement('div');
         wrapper.className = 'course-checkout-block__participant-field';

         const safeFieldName = `participant_${participant.id}_${fieldName}`;

         const label = document.createElement('label');
         label.setAttribute('for', `${this.dataset.blockId}-${safeFieldName}`);
         label.textContent = labelText;

         const input = document.createElement('input');
         input.id = `${this.dataset.blockId}-${safeFieldName}`;
         input.name = safeFieldName;
         input.type = inputType;
         input.autocomplete = autocomplete;
         if (fieldName === 'taxCode')
         {
            input.autocapitalize = 'characters';
         }
         input.dataset.participantId = String(participant.id);
         input.dataset.participantField = fieldName;
         input.value = participant[fieldName] || '';
         input.required = true;

         wrapper.appendChild(label);
         const inputWrapper = document.createElement('div');
         inputWrapper.className = 'course-checkout-block__participant-input-wrapper';
         inputWrapper.appendChild(input);

         wrapper.appendChild(inputWrapper);

         return wrapper;
      }

      handleAddMember()
      {
         this.addParticipant('socio');
      }

      addParticipant(type)
      {
         if (!this.canAddParticipant(type)) return;

         this.participants.push({
            id: this.nextParticipantId,
            type,
            firstName: '',
            lastName: '',
         });
         this.nextParticipantId += 1;

         this.syncQuantityFromParticipants();
         this.renderParticipants();
      }

      handleParticipantInput(event)
      {
         const input = event.target;
         if (!(input instanceof HTMLInputElement)) return;

         const participantId = Number.parseInt(input.dataset.participantId || '', 10);
         const participantField = input.dataset.participantField;
         if (!Number.isFinite(participantId) || !participantField) return;

         const participant = this.participants.find((entry) => entry.id === participantId);
         if (!participant) return;

         participant[participantField] = input.value;
      }

      handleParticipantAction(event)
      {
         const target = event.target;
         if (!(target instanceof Element)) return;

         const removeButton = target.closest('[data-remove-participant]');
         if (!(removeButton instanceof HTMLElement)) return;

         const participantId = Number.parseInt(removeButton.dataset.removeParticipant || '', 10);
         if (!Number.isFinite(participantId)) return;

         this.participants = this.participants.filter((participant) => participant.id !== participantId);
         this.syncQuantityFromParticipants();
         this.renderParticipants();
         this.updateSubmitButtonState();
      }

      updateActionButtons()
      {
         if (this.addMemberButton)
         {
            this.addMemberButton.disabled = !this.canAddParticipant('socio');
         }
      }

      updateSubmitButtonState()
      {
         if (!this.submitButton) return;

         const hasVariantId = Boolean(this.variantInput?.value);
         const maxQuantity = this.getMaxQuantity();
         const shouldDisable =
            this.isSubmitting ||
            !this.isCheckoutDateValid ||
            !hasVariantId ||
            !this.isVariantAvailable ||
            (maxQuantity !== null && this.getQuantity() > maxQuantity);

         this.submitButton.disabled = shouldDisable;
      }

      showError(message)
      {
         if (!this.errorMessage) return;
         this.errorMessage.textContent = message;
         this.errorMessage.classList.remove('hidden');
      }

      clearError()
      {
         if (!this.errorMessage) return;
         this.errorMessage.textContent = '';
         this.errorMessage.classList.add('hidden');
      }

      setSubmittingState(isSubmitting)
      {
         if (!this.submitButton) return;

         this.isSubmitting = isSubmitting;
         this.updateSubmitButtonState();
         this.submitButton.setAttribute('aria-busy', isSubmitting ? 'true' : 'false');

         if (this.submitButtonText)
         {
            this.submitButtonText.textContent = isSubmitting ? this.loadingButtonText : this.defaultButtonText;
         }
      }

      buildExtraParticipantAttributeEntries()
      {
         const attributes = [];
         if (this.isRegistrationMode)
         {
            const registrants = this.participants.filter((participant) => participant.type === 'iscritto');

            registrants.forEach((participant, index) =>
            {
               const attributePrefix = `iscritto_aggiuntivo_${index + 1}`;
               const values = {
                  nome: participant.firstName,
                  cognome: participant.lastName,
                  email: participant.email,
                  codice_fiscale: participant.taxCode,
               };

               Object.entries(values).forEach(([fieldName, value]) =>
               {
                  const normalizedValue = String(value || '').trim();
                  if (normalizedValue)
                  {
                     attributes.push([`${attributePrefix}_${fieldName}`, normalizedValue]);
                  }
               });
            });

            return attributes;
         }

         const orderedParticipants = this.participants.filter((participant) => participant.type === 'socio');
         let memberIndex = 0;

         for (const participant of orderedParticipants)
         {
            const firstName = String(participant.firstName || '').trim();
            const lastName = String(participant.lastName || '').trim();

            memberIndex += 1;
            if (firstName)
            {
               attributes.push([`partecipante_aggiuntivo_${memberIndex}_nome`, firstName]);
            }
            if (lastName)
            {
               attributes.push([`partecipante_aggiuntivo_${memberIndex}_cognome`, lastName]);
            }
         }

         return attributes;
      }

      extractAttributeName(fieldName)
      {
         if (typeof fieldName !== 'string') return null;
         if (!fieldName.startsWith('attributes[') || !fieldName.endsWith(']')) return null;

         return fieldName.slice(11, -1).trim();
      }

      buildCartAttributeEntries()
      {
         const formData = new FormData(this.form);
         const attributes = [];

         for (const [fieldName, value] of formData.entries())
         {
            const attributeName = this.extractAttributeName(fieldName);
            if (!attributeName) continue;

            const normalizedValue = String(value || '').trim();
            if (normalizedValue !== '')
            {
               attributes.push([attributeName, normalizedValue]);
            }
         }

         attributes.push(...this.buildExtraParticipantAttributeEntries());

         return attributes;
      }

      buildCartAttributesPayload()
      {
         const legacyAttributeNames = [
            'Nome',
            'Cognome',
            'Numero tessera',
            'Codice Fiscale',
            'Email',
            'Indirizzo',
            'Città',
            'Provincia',
            'CAP',
            'Nazione',
            'Data di Nascita',
            'Città di Nascita',
            'Provincia di Nascita',
            'Telefono',
            'Partecipanti aggiuntivi',
            'Accompagnatori',
         ];
         const payload = legacyAttributeNames.reduce((attributes, attributeName) =>
         {
            attributes[attributeName] = '';
            return attributes;
         }, {});

         return this.buildCartAttributeEntries().reduce((attributes, [attributeName, attributeValue]) =>
         {
            attributes[attributeName] = attributeValue;
            return attributes;
         }, payload);
      }

      extractPropertyName(fieldName)
      {
         if (typeof fieldName !== 'string') return null;
         if (!fieldName.startsWith('properties[') || !fieldName.endsWith(']')) return null;

         return fieldName.slice(11, -1).trim();
      }

      buildLineItemPropertiesPayload()
      {
         const formData = new FormData(this.form);
         const properties = {};

         for (const [fieldName, value] of formData.entries())
         {
            const propertyName = this.extractPropertyName(fieldName);
            if (!propertyName) continue;

            const normalizedValue = String(value || '').trim();
            if (normalizedValue !== '')
            {
               properties[propertyName] = normalizedValue;
            }
         }

         return properties;
      }

      buildCheckoutItems(variantId, quantity)
      {
         const properties = this.buildLineItemPropertiesPayload();
         const item = {
            id: variantId,
            quantity,
         };

         if (Object.keys(properties).length > 0)
         {
            item.properties = properties;
         }

         return [
            item,
         ];
      }

      getRootPath()
      {
         const rootPath = window.Shopify?.routes?.root || '/';
         return rootPath.endsWith('/') ? rootPath : `${rootPath}/`;
      }

      getCartAddUrl()
      {
         return window.Theme?.routes?.cart_add_url || `${this.getRootPath()}cart/add.js`;
      }

      getCartUpdateUrl()
      {
         return window.Theme?.routes?.cart_update_url || `${this.getRootPath()}cart/update`;
      }

      getCartClearUrl()
      {
         return `${this.getRootPath()}cart/clear.js`;
      }

      getCheckoutSearchParams()
      {
         const formData = new FormData(this.form);
         const isCustomerCheckout = this.dataset.checkoutPrefillSource === 'customer';
         const formFields = isCustomerCheckout
            ? {
               email: this.dataset.checkoutEmailField || 'customer_email',
               firstName: 'customer_first_name',
               lastName: 'customer_last_name',
            }
            : { email: 'attributes[email]', firstName: 'attributes[nome]', lastName: 'attributes[cognome]' };
         const mappings = [
            ['checkout[email]', formFields.email],
            ['checkout[shipping_address][first_name]', formFields.firstName],
            ['checkout[shipping_address][last_name]', formFields.lastName],
         ];
         const searchParams = new URLSearchParams();

         mappings.forEach(([checkoutField, formField]) => {
            const value = String(formData.get(formField) || '').trim();
            if (value) searchParams.set(checkoutField, value);
         });

         return searchParams;
      }

      getCheckoutUrl()
      {
         const checkoutUrl = `${this.getRootPath()}checkout`;
         const searchParams = this.getCheckoutSearchParams();
         const queryString = searchParams.toString();
         return queryString ? `${checkoutUrl}?${queryString}` : checkoutUrl;
      }

      encodeBase64Json(payload)
      {
         const bytes = new TextEncoder().encode(JSON.stringify(payload));
         let binary = '';

         for (const byte of bytes)
         {
            binary += String.fromCharCode(byte);
         }

         return window.btoa(binary);
      }

      getCartPermalinkCheckoutUrl(variantId, quantity)
      {
         const cartPermalinkUrl = `${this.getRootPath()}cart/${variantId}:${quantity}`;
         const searchParams = this.getCheckoutSearchParams();
         const properties = this.buildLineItemPropertiesPayload();
         const attributes = this.buildCartAttributesPayload();

         if (Object.keys(properties).length > 0)
         {
            searchParams.set('properties', this.encodeBase64Json(properties));
         }

         for (const [attributeName, attributeValue] of Object.entries(attributes))
         {
            searchParams.set(`attributes[${attributeName}]`, attributeValue);
         }

         const queryString = searchParams.toString();
         return queryString ? `${cartPermalinkUrl}?${queryString}` : cartPermalinkUrl;
      }

      async postCartJson(url, payload = {})
      {
         const response = await fetch(url, {
            method: 'POST',
            headers: {
               'Content-Type': 'application/json',
               Accept: 'application/json',
            },
            body: JSON.stringify(payload),
         });

         let data = null;

         try
         {
            data = await response.json();
         } catch (error)
         {
            data = null;
         }

         if (!response.ok || data?.status)
         {
            const errorMessage = data?.description || data?.message || `HTTP ${response.status}`;
            throw new Error(errorMessage);
         }

         return data;
      }

      async submitCheckoutCart(variantId, quantity)
      {
         // Il prefill del checkout è supportato da Shopify sui cart permalink.
         // Questo ramo è attivo esclusivamente per il blocco "Regala".
         if (this.dataset.checkoutPrefillSource === 'customer')
         {
            return this.getCartPermalinkCheckoutUrl(variantId, quantity);
         }

         const items = this.buildCheckoutItems(variantId, quantity);

         await this.postCartJson(this.getCartClearUrl());

         await this.postCartJson(this.getCartAddUrl(), { items });

         const attributes = this.buildCartAttributesPayload();
         if (Object.keys(attributes).length > 0)
         {
            await this.postCartJson(this.getCartUpdateUrl(), { attributes });
         }

         return this.getCheckoutUrl();
      }

      async handleSubmit(event)
      {
         event.preventDefault();

         if (!this.isCheckoutDateValid) return;
         if (!this.form.reportValidity()) return;

         const variantId = Number.parseInt(this.variantInput?.value || '', 10);
         const quantity = this.getQuantity();

         if (!Number.isFinite(variantId) || variantId <= 0)
         {
            this.showError('Variante non valida.');
            return;
         }

         this.clearError();
         this.setSubmittingState(true);

         try
         {
            const checkoutUrl = await this.submitCheckoutCart(variantId, quantity);
            window.location.assign(checkoutUrl);
         } catch (error)
         {
            this.showError(error instanceof Error ? error.message : 'Si e verificato un errore inatteso.');
            this.setSubmittingState(false);
         }
      }
   }

   customElements.define('course-checkout-form', CourseCheckoutForm);
}
